import { API_URL } from '../utils/config';
import { v4 as uuidv4 } from 'uuid';

// One-time migration: checkoutData moved from localStorage to sessionStorage.
// Carry over a pre-migration copy (applied discounts) and clear the legacy key
// so returning customers keep their discount and no stale data lingers.
try {
  const legacyCheckoutData = localStorage.getItem('checkoutData');
  if (legacyCheckoutData) {
    if (!sessionStorage.getItem('checkoutData')) {
      sessionStorage.setItem('checkoutData', legacyCheckoutData);
    }
    localStorage.removeItem('checkoutData');
  }
} catch (error) {
  // Storage unavailable — nothing to migrate
}

// Helper to get or create session ID from localStorage
const getSessionId = () => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem('cart_session_id', sessionId);
    // console.log(`Created new cart session ID: ${sessionId}`); // Removed log
  } else {
    // console.log(`Using existing cart session ID: ${sessionId}`); // Removed log
  }
  
  // Also ensure the session ID is set to a global window property to make it accessible
  // in case localStorage is cleared or not working correctly
  window.__cart_session_id = sessionId;
  
  return sessionId;
};

// Helper to extract Stripe session ID from URL if present
const getStripeSessionId = () => {
  // Check if we're on a success or cancel page - check multiple possible URL patterns
  const isSuccessPage = window.location.pathname.includes('/order-success') || 
                        window.location.pathname.includes('/order/success') ||
                        window.location.pathname.includes('/success');
  
  const isCancelPage = window.location.pathname.includes('/order-cancel') ||
                       window.location.pathname.includes('/order/cancel') ||
                       window.location.pathname.includes('/cancel');
  
  const isReturnPage = isSuccessPage || isCancelPage;
  
  // Get session ID from URL parameters - check multiple possible parameter names
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id') || 
                    urlParams.get('stripe_session') || 
                    urlParams.get('sessionId') || 
                    urlParams.get('payment_intent');
  
  // If we have a session ID in the URL and we're on a return page, use it and save it
  if (sessionId && isReturnPage) {
    // console.log(`Found Stripe session ID in URL: ${sessionId}`); // Removed log
    localStorage.setItem('stripe_session_id', sessionId);
    // Also set a flag to indicate this is the first load after returning from Stripe
    localStorage.setItem('stripe_return_processed', 'false');
    return sessionId;
  }
  
  // If no session ID in URL params, check if it's in the path (Stripe redirects sometimes include it in the path)
  if (!sessionId && isReturnPage) {
    // Extract from path if it matches various patterns
    const successPathMatch = window.location.pathname.match(/\/success\/(cs_[a-zA-Z0-9_]+)/);
    const cancelPathMatch = window.location.pathname.match(/\/cancel\/(cs_[a-zA-Z0-9_]+)/);
    const orderSuccessPathMatch = window.location.pathname.match(/\/order[\/-]success\/(cs_[a-zA-Z0-9_]+)/);
    const orderCancelPathMatch = window.location.pathname.match(/\/order[\/-]cancel\/(cs_[a-zA-Z0-9_]+)/);
    
    // Try all possible path patterns
    const extractedId = (successPathMatch && successPathMatch[1]) || 
                        (cancelPathMatch && cancelPathMatch[1]) ||
                        (orderSuccessPathMatch && orderSuccessPathMatch[1]) ||
                        (orderCancelPathMatch && orderCancelPathMatch[1]);
    
    if (extractedId) {
      // console.log(`Found Stripe session ID in path: ${extractedId}`); // Removed log
      localStorage.setItem('stripe_session_id', extractedId);
      localStorage.setItem('stripe_return_processed', 'false');
      return extractedId;
    }
  }
  
  // Check if we need to use the stored session ID one time
  const savedStripeSession = localStorage.getItem('stripe_session_id');
  const returnProcessed = localStorage.getItem('stripe_return_processed');
  
  if (savedStripeSession && returnProcessed === 'false') {
    // console.log(`Using stored Stripe session ID once: ${savedStripeSession}`); // Removed log
    // Mark as processed so we don't use it again
    localStorage.setItem('stripe_return_processed', 'true');
    return savedStripeSession;
  } else if (savedStripeSession && returnProcessed === 'true') {
    // We've already processed this return, clear the session ID
    // console.log('Clearing Stripe session ID after processing'); // Removed log
    localStorage.removeItem('stripe_session_id');
    localStorage.removeItem('stripe_return_processed');
    return null;
  }
  
  return null;
};

const toFiniteNumber = (value, fallback) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeCartTotals = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  data.cartTotal = toFiniteNumber(data.cartTotal, 0);
  data.finalTotal = toFiniteNumber(data.finalTotal, data.cartTotal);
  data.analyticsTotal = toFiniteNumber(data.totalAfterDiscounts, data.finalTotal);

  return data;
};

// Get all cart items
// Added optional customerDetails parameter
export const getCartItems = async (customerDetails = null) => {
  try {
    const sessionId = getSessionId();
    const stripeSessionId = getStripeSessionId();
    
    // Prepare request body with session IDs
    const requestBody = { sessionId };

    // Add customerDetails if provided
    if (customerDetails) {
      requestBody.customerDetails = customerDetails;
      // console.log('Including customerDetails in getCartItems request:', customerDetails); // Removed log
    }
    
    // Check if this is a return from Stripe
    const isSuccessPage = window.location.pathname.includes('/order-success');
    const isCancelPage = window.location.pathname.includes('/order-cancel');
    const isFromStripe = isSuccessPage || isCancelPage || stripeSessionId;
    
    // Add fromStripe flag if this request is coming from a Stripe redirect
    if (isFromStripe) {
      requestBody.fromStripe = true;
      // console.log('Including fromStripe flag in cart request'); // Removed log
    }
    
    // Add Stripe session ID if available (only if it's not null)
    if (stripeSessionId) {
      requestBody.stripeSessionId = stripeSessionId;
      // console.log(`Including Stripe session ID in cart request: ${stripeSessionId}`); // Removed log
    } else {
      // console.log('No Stripe session ID included in cart request'); // Removed log
    }
    
    // Check if there's a gift card or coupon code in sessionStorage
    try {
      const savedCheckoutData = sessionStorage.getItem('checkoutData');
      if (savedCheckoutData) {
        const parsedData = JSON.parse(savedCheckoutData);
        
        // Include gift card code if available
        if (parsedData.discountInfo?.appliedGiftCard) {
          requestBody.giftCardCode = parsedData.discountInfo.appliedGiftCard.code;
          // console.log(`Including gift card code in cart request: ${requestBody.giftCardCode}`); // Removed log
        } else if (parsedData.discountInfo?.giftCardCode) {
          requestBody.giftCardCode = parsedData.discountInfo.giftCardCode;
          // console.log(`Including gift card code in cart request: ${requestBody.giftCardCode}`); // Removed log
        }
        
        // Include coupon code ONLY if it's actually applied (not just in the input field)
        if (parsedData.discountInfo?.appliedCoupon) {
          requestBody.couponCode = parsedData.discountInfo.appliedCoupon.code;
          // console.log(`Including coupon code in cart request: ${requestBody.couponCode}`); // Removed log
        }
        // Removed the else if branch that was sending couponCode even when not applied
      }
    } catch (error) {
      // console.error('Error reading discount info from sessionStorage:', error); // Removed error log
      // Continue without discount codes if there's an error
    }
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Stripe session ID to headers if available (only if it's not null)
    if (stripeSessionId) {
      headers['x-stripe-session'] = stripeSessionId;
    }
    
    const response = await fetch(`${API_URL}/cart/items`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 403) {
        // console.error('Session validation failed. This might be due to an expired session.'); // Removed error log
      }
      throw new Error('Failed to fetch cart items');
    }

    const data = await response.json();
    return normalizeCartTotals(data);
  } catch (error) {
    console.error('[cartService] Error fetching cart items:', error); // Log error
    throw error;
  }
}; // End of getCartItems function

// Add item to cart
// Added optional customerDetails, giftCardData, selectedVariationName, productComment, productCommentCustom parameters
export const addToCart = async (
  productId, 
  quantity = 1, 
  giftCardData = null, 
  customerDetails = null, 
  selectedVariationName = null,
  productComment = null,        // Added standard comment param
  productCommentCustom = null   // Added custom comment param
) => {
  try {
    const sessionId = getSessionId();
    const stripeSessionId = getStripeSessionId();
    
    // Check if this is a return from Stripe
    const isSuccessPage = window.location.pathname.includes('/order-success');
    const isCancelPage = window.location.pathname.includes('/order-cancel');
    const isFromStripe = isSuccessPage || isCancelPage || stripeSessionId;
    
    // Prepare request body
    const requestBody = {
      productId,
      quantity,
      sessionId,
      stripeSessionId: stripeSessionId || undefined,
      fromStripe: isFromStripe || undefined,
      // Add customerDetails if provided
      ...(customerDetails && { customerDetails }),
      // Add giftCardData if provided
      ...(giftCardData && { giftCardData }),
      // Add selectedVariationName if provided
      ...(selectedVariationName && { selectedVariationName }),
      // Add comments if provided
      ...(productComment && { productComment }),
      ...(productCommentCustom && { productCommentCustom })
    };
    
    // Check if there's a gift card or coupon code in sessionStorage
    try {
      const savedCheckoutData = sessionStorage.getItem('checkoutData');
      if (savedCheckoutData) {
        const parsedData = JSON.parse(savedCheckoutData);
        
        // Include gift card code if available
        if (parsedData.discountInfo?.appliedGiftCard) {
          requestBody.giftCardCode = parsedData.discountInfo.appliedGiftCard.code;
          // console.log(`Including gift card code in add to cart request: ${requestBody.giftCardCode}`); // Removed log
        } else if (parsedData.discountInfo?.giftCardCode) {
          requestBody.giftCardCode = parsedData.discountInfo.giftCardCode;
          // console.log(`Including gift card code in add to cart request: ${requestBody.giftCardCode}`); // Removed log
        }
        
        // Include coupon code ONLY if it's actually applied
        if (parsedData.discountInfo?.appliedCoupon) {
          requestBody.couponCode = parsedData.discountInfo.appliedCoupon.code;
          // console.log(`Including coupon code in add to cart request: ${requestBody.couponCode}`); // Removed log
        }
        // Removed the else if branch that was sending couponCode even when not applied
      }
    } catch (error) {
      // console.error('Error reading discount info from sessionStorage:', error); // Removed error log
      // Continue without discount codes if there's an error
    }
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Stripe session ID to headers if available
    if (stripeSessionId) {
      headers['x-stripe-session'] = stripeSessionId;
    }
    
    const response = await fetch(`${API_URL}/cart/add`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add item to cart');
    }

    const data = await response.json();
    return normalizeCartTotals(data);
  } catch (error) {
    
    throw error;
  }
};

// Update cart item quantity
// Added optional customerDetails parameter
export const updateCartItem = async (cartId, quantity, customerDetails = null) => {
  try {
    const sessionId = getSessionId();
    const stripeSessionId = getStripeSessionId();
    
    // Check if this is a return from Stripe
    const isSuccessPage = window.location.pathname.includes('/order-success');
    const isCancelPage = window.location.pathname.includes('/order-cancel');
    const isFromStripe = isSuccessPage || isCancelPage || stripeSessionId;
    
    // Prepare request body
    const requestBody = {
      cartId,
      quantity,
      sessionId,
      stripeSessionId: stripeSessionId || undefined,
      fromStripe: isFromStripe || undefined,
      // Add customerDetails if provided
      ...(customerDetails && { customerDetails })
    };
    
    // Check if there's a gift card or coupon code in sessionStorage
    try {
      const savedCheckoutData = sessionStorage.getItem('checkoutData');
      if (savedCheckoutData) {
        const parsedData = JSON.parse(savedCheckoutData);
        
        // Include gift card code if available
        if (parsedData.discountInfo?.appliedGiftCard) {
          requestBody.giftCardCode = parsedData.discountInfo.appliedGiftCard.code;
          // console.log(`Including gift card code in update cart request: ${requestBody.giftCardCode}`); // Removed log
        } else if (parsedData.discountInfo?.giftCardCode) {
          requestBody.giftCardCode = parsedData.discountInfo.giftCardCode;
          // console.log(`Including gift card code in update cart request: ${requestBody.giftCardCode}`); // Removed log
        }
        
        // Include coupon code ONLY if it's actually applied
        if (parsedData.discountInfo?.appliedCoupon) {
          requestBody.couponCode = parsedData.discountInfo.appliedCoupon.code;
          // console.log(`Including coupon code in update cart request: ${requestBody.couponCode}`); // Removed log
        }
        // Removed the else if branch that was sending couponCode even when not applied
      }
    } catch (error) {
      // console.error('Error reading discount info from sessionStorage:', error); // Removed error log
      // Continue without discount codes if there's an error
    }
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Stripe session ID to headers if available
    if (stripeSessionId) {
      headers['x-stripe-session'] = stripeSessionId;
    }
    
    const response = await fetch(`${API_URL}/cart/update`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update cart item');
    }

    const data = await response.json();
    return normalizeCartTotals(data);
  } catch (error) {
    
    throw error;
  }
};

// Remove item from cart
// Added optional customerDetails parameter
export const removeFromCart = async (cartId, customerDetails = null) => {
  try {
    const sessionId = getSessionId();
    const stripeSessionId = getStripeSessionId();
    
    // Check if this is a return from Stripe
    const isSuccessPage = window.location.pathname.includes('/order-success');
    const isCancelPage = window.location.pathname.includes('/order-cancel');
    const isFromStripe = isSuccessPage || isCancelPage || stripeSessionId;
    
    // Prepare request body
    const requestBody = {
      cartId,
      sessionId,
      stripeSessionId: stripeSessionId || undefined,
      fromStripe: isFromStripe || undefined,
      // Add customerDetails if provided
      ...(customerDetails && { customerDetails })
    };
    
    // Check if there's a gift card or coupon code in sessionStorage
    try {
      const savedCheckoutData = sessionStorage.getItem('checkoutData');
      if (savedCheckoutData) {
        const parsedData = JSON.parse(savedCheckoutData);
        
        // Include gift card code if available
        if (parsedData.discountInfo?.appliedGiftCard) {
          requestBody.giftCardCode = parsedData.discountInfo.appliedGiftCard.code;
          // console.log(`Including gift card code in remove from cart request: ${requestBody.giftCardCode}`); // Removed log
        } else if (parsedData.discountInfo?.giftCardCode) {
          requestBody.giftCardCode = parsedData.discountInfo.giftCardCode;
          // console.log(`Including gift card code in remove from cart request: ${requestBody.giftCardCode}`); // Removed log
        }
        
        // Include coupon code ONLY if it's actually applied
        if (parsedData.discountInfo?.appliedCoupon) {
          requestBody.couponCode = parsedData.discountInfo.appliedCoupon.code;
          // console.log(`Including coupon code in remove from cart request: ${requestBody.couponCode}`); // Removed log
        }
        // Removed the else if branch that was sending couponCode even when not applied
      }
    } catch (error) {
      // console.error('Error reading discount info from sessionStorage:', error); // Removed error log
      // Continue without discount codes if there's an error
    }
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Stripe session ID to headers if available
    if (stripeSessionId) {
      headers['x-stripe-session'] = stripeSessionId;
    }
    
    const response = await fetch(`${API_URL}/cart/remove`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to remove item from cart');
    }

    const data = await response.json();
    return normalizeCartTotals(data);
  } catch (error) {
    
    throw error;
  }
};

// Clear cart (remove all items)
export const clearCart = async () => {
  try {
    const sessionId = getSessionId();
    const stripeSessionId = getStripeSessionId();
    
    // Check if this is a return from Stripe
    const isSuccessPage = window.location.pathname.includes('/order-success');
    const isCancelPage = window.location.pathname.includes('/order-cancel');
    const isFromStripe = isSuccessPage || isCancelPage || stripeSessionId;
    
    // Prepare request body
    const requestBody = {
      sessionId,
      stripeSessionId: stripeSessionId || undefined,
      fromStripe: isFromStripe || undefined
    };
    
    // Check if there's a gift card or coupon code in sessionStorage
    try {
      const savedCheckoutData = sessionStorage.getItem('checkoutData');
      if (savedCheckoutData) {
        const parsedData = JSON.parse(savedCheckoutData);
        
        // Include gift card code if available
        if (parsedData.discountInfo?.appliedGiftCard) {
          requestBody.giftCardCode = parsedData.discountInfo.appliedGiftCard.code;
          // console.log(`Including gift card code in clear cart request: ${requestBody.giftCardCode}`); // Removed log
        } else if (parsedData.discountInfo?.giftCardCode) {
          requestBody.giftCardCode = parsedData.discountInfo.giftCardCode;
          // console.log(`Including gift card code in clear cart request: ${requestBody.giftCardCode}`); // Removed log
        }
        
        // Include coupon code ONLY if it's actually applied
        if (parsedData.discountInfo?.appliedCoupon) {
          requestBody.couponCode = parsedData.discountInfo.appliedCoupon.code;
          // console.log(`Including coupon code in clear cart request: ${requestBody.couponCode}`); // Removed log
        }
        // Removed the else if branch that was sending couponCode even when not applied
      }
    } catch (error) {
      // console.error('Error reading discount info from sessionStorage:', error); // Removed error log
      // Continue without discount codes if there's an error
    }
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Stripe session ID to headers if available
    if (stripeSessionId) {
      headers['x-stripe-session'] = stripeSessionId;
    }
    
    const response = await fetch(`${API_URL}/cart/clear`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to clear cart');
    }

    const data = await response.json();
    return normalizeCartTotals(data);
  } catch (error) {
    
    throw error;
  }
};

// Count items in cart
export const getCartCount = async () => {
  try {
    const { cartItems } = await getCartItems();
    const count = cartItems ? cartItems.length : 0;
    return count;
  } catch (error) {
    console.error('[cartService] Error in getCartCount:', error); // Log error
    return 0;
  }
};

// Get product cart activity (social proof feature)
export const fetchProductCartActivity = async (productId, days = 7) => {
  try {
    const sessionId = getSessionId(); // Get current session to exclude from count
    const response = await fetch(
      `${API_URL}/cart/activity/${productId}?days=${days}&sessionId=${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      console.error('Error fetching cart activity:', response.statusText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching cart activity:', error);
    return null;
  }
};
